import createMuiTheme from "@material-ui/core/styles/createMuiTheme";
import { MuiPickersOverrides } from "@material-ui/pickers/typings/overrides";

type overridesNameToClassKey = {
  [P in keyof MuiPickersOverrides]: keyof MuiPickersOverrides[P];
};

declare module "@material-ui/core/styles/overrides" {
  interface ComponentNameToClassKey extends overridesNameToClassKey {}
}

const theme = createMuiTheme({
  palette: {
    primary: {
      main: "#231f20",
      dark: "#080707",
    },
    secondary: {
      main: "#e6c224",
      dark: "#c1a116",
      contrastText: "#ffffff",
    },
    success: {
      main: "#7ec884",
      dark: "#59b962",
      contrastText: "#ffffff",
    },
    error: {
      main: "#f8202d",
      dark: "#a90000",
      light: "#ffddd1",
      contrastText: "#ffffff",
    },
    warning: {
      main: "#ffc10d",
      dark: "#fdc009",
      contrastText: "#ffffff",
    },
    info: {
      main: "#2f80ed",
      dark: "#0074cd",
      contrastText: "#ffffff",
    },
    background: {
      default: "#ffffff",
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 520,
      md: 1200,
      lg: 1480,
      xl: 1900,
    },
  },
  shape: {
    borderRadius: 15,
  },
  overrides: {
    MuiInputBase: {
      input: {
        "&.Mui-disabled": {
          backgroundColor: "#bcbcbc33",
        },
      },
    },
    MuiDialog: {
      paper: {
        margin: 12,
      },
    },
    MuiAppBar: {
      root: {
        padding: "1rem 1.875rem",
      },
    },
    MuiTabs: {
      root: {
        borderBottom: "1px solid #dfdcd6",
      },
      indicator: {
        backgroundColor: "#957F2F",
        height: 4,
      },
    },
    MuiTab: {
      root: {
        textTransform: "none",
        fontSize: 16,
        fontWeight: 400,
        "&.Mui-selected": {
          fontWeight: 500,
        },
      },
    },
    MuiFormControlLabel: {
      label: {
        color: "#000",
        fontSize: 14,
        fontWeight: 400,
      },
    },
    MuiPaper: {
      root: {
        color: "#231f20",
      },
      elevation1: {
        padding: "1rem",
        boxShadow: "0 0 4px 0 #c2c9d6",
      },
    },
    MuiContainer: {
      root: {
        paddingLeft: 15,
        paddingRight: 15,
      },
      maxWidthLg: {
        "@media (min-width: 1200px)": {
          "& .MuiGrid-container.myAccountPageContainer": {
            "& > .MuiGrid-item": {
              "&.MuiGrid-grid-md-3": {
                maxWidth: "298px",
              },
              "&.MuiGrid-grid-md-9": {
                maxWidth: "unset",
                flexGrow: 1,
              },
            },
          },
        },
      },
    },
    MuiBreadcrumbs: {
      root: {
        marginBottom: "1rem",
        "& a": {
          color: "inherit",
        },
      },
    },
    MuiBottomNavigation: {
      root: {
        zIndex: 1000,
        boxShadow: "0 -4px 8px 0 rgba(0, 0, 0, 0.11)",
        "& a": {
          color: "#858997",
        },
      },
    },
    MuiButton: {
      root: {
        padding: "8px 40px!important",
        textTransform: "unset",
        "&:hover": {
          boxShadow: "none!important",
          textDecoration: "none!important",
        },
      },
      containedSecondary: {
        color: "#ffffff!important",
        "&:hover": {
          backgroundColor: "#ccab17",
        },
      },
      contained: {
        boxShadow: "none",
        fontWeight: "bold",
      },
      text: {
        "&:hover": {
          textDecoration: "underline!important",
          backgroundColor: "transparent!important",
        },
      },
    },
    MuiIcon: {
      fontSizeSmall: {
        fontSize: "16px",
      },
    },
    MuiIconButton: {
      root: {
        color: "#858997",
      },
      sizeSmall: {
        "& .MuiIcon-root": {
          fontSize: "1em",
        },
      },
    },
    MuiTouchRipple: {
      child: {
        backgroundColor: "#858997",
      },
    },
    MuiInputAdornment: {
      root: {},
      positionEnd: {
        right: 1,
        position: "absolute",
        "& > .MuiIconButton-root": {
          borderRadius: 0,
          borderTopRightRadius: 16,
          borderBottomRightRadius: 16,
          margin: 0,
          padding: "10px 12px 10px 12px",
        },
      },
    },
    MuiSnackbarContent: {
      root: {},
    },
    MuiCardHeader: {
      root: {
        padding: 0,
        marginBottom: "16px",
      },
      title: {
        fontWeight: 600,
      },
      action: {
        marginTop: 0,
        marginRight: 0,
      },
    },
    MuiCardContent: {
      root: {
        padding: 0,
      },
    },
    MuiPickersDay: {
      daySelected: {
        backgroundColor: "#2f80ed",
        borderRadius: 9,
        "&:hover": {
          backgroundColor: "#2f80ed",
        },
      },
      current: {
        "&::before": {
          content: '""',
          width: 6,
          height: 6,
          borderRadius: 3,
          border: "solid 1px #fff",
          backgroundColor: "#ec634d",
          position: "absolute",
          bottom: 0,
          left: 15,
        },
      },
    },
  },
  typography: {
    h1: {
      fontSize: 30,
      fontWeight: 700,
      marginBottom: 10,
    },
    h2: {
      fontSize: 25,
      marginBottom: 5,
    },
    h3: {
      fontWeight: 700,
      fontSize: 20,
    },
    h4: {
      fontWeight: 700,
      fontSize: 16,
    },
  },
});

export default theme;

export const dashboardTheme = theme;
export const publicTheme = theme;
